// =====================================================================
// Faz I (2026-05-06) — Kupon Yönetim Paneli
// =====================================================================
// Admin: kupon listele, yeni kupon, aktif/pasif toggle, kullanım sayıları.
//
// Endpoint'ler:
//   GET  /api/coupons/admin/list             -> { coupons: [...] }
//   POST /api/coupons/admin/create           -> body: {code,type,value,...}
//   PUT  /api/coupons/admin/:id/toggle       -> aktif/pasif anahtar
//
// Coupon schema (DB):
//   id, code, type (percent|fixed|free_shipping), value,
//   min_order_amount, max_discount, user_id, segment,
//   usage_limit, used_count, per_user_limit,
//   starts_at, expires_at, is_active, created_at
//
// NPS_THANKS5 gibi otomatik üretilmiş kuponlar burada da görünür
// (NPS promoter teşekkür kuponu, mail HTML üzerinden dağıtılır).
// =====================================================================
import React, { useEffect, useState } from "react";
import {
  Box, Card, Typography, Grid, Chip, Table, TableHead, TableRow, TableCell,
  TableBody, CircularProgress, Alert, Button, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Switch
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import API from "../services/api";
import toast from "react-hot-toast";

const fmtDate = (s) => s ? new Date(s).toLocaleDateString("tr-TR") : "—";
const fmtMoney = (v) => v == null ? "—" :
  Number(v).toLocaleString("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: 2 }) + " ₺";

// type → görünür etiket + renk + value formatı
const TYPE_META = {
  percent:       { label: "Yüzde",        color: "#3b82f6", fmt: (v) => `%${v}` },
  fixed:         { label: "Sabit Tutar",  color: "#8b5cf6", fmt: (v) => `${v} ₺` },
  free_shipping: { label: "Ücretsiz Kargo", color: "#10b981", fmt: () => "Kargo Bedava" }
};

// Bir kupon "süresi dolmuş" mu? (expires_at past)
const isExpired = (c) => c.expires_at && new Date(c.expires_at) < new Date();
// Limit dolmuş mu?
const isLimitFull = (c) => c.usage_limit && c.used_count >= c.usage_limit;

// İlk yükleme + yeni kupon formunun başlangıç değerleri
const emptyForm = {
  code: "", type: "percent", value: "",
  min_order_amount: "", max_discount: "",
  usage_limit: "", per_user_limit: "1",
  expires_at: "", segment: ""
};

export default function Coupons() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    API.get("/api/coupons/admin/list")
      .then(r => { setCoupons(r.data.coupons || []); setErr(null); })
      .catch(e => setErr(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const toggle = async (id) => {
    try {
      await API.put(`/api/coupons/admin/${id}/toggle`);
      toast.success("Durum güncellendi");
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const submit = async () => {
    if (!form.code || !form.type || form.value === "") {
      toast.error("Kod, tip ve değer zorunlu");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        code: form.code.toUpperCase().trim(),
        type: form.type,
        value: parseFloat(form.value),
        min_order_amount: form.min_order_amount ? parseFloat(form.min_order_amount) : null,
        max_discount: form.max_discount ? parseFloat(form.max_discount) : null,
        usage_limit: form.usage_limit ? parseInt(form.usage_limit, 10) : null,
        per_user_limit: form.per_user_limit ? parseInt(form.per_user_limit, 10) : 1,
        expires_at: form.expires_at || null,
        segment: form.segment || null
      };
      await API.post("/api/coupons/admin/create", payload);
      toast.success(`Kupon oluşturuldu: ${payload.code}`);
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  // KPI: toplam kupon, aktif, kullanım sayısı, ortalama kullanım/kupon
  const kpi = {
    total: coupons.length,
    active: coupons.filter(c => c.is_active && !isExpired(c) && !isLimitFull(c)).length,
    totalUsage: coupons.reduce((s, c) => s + (Number(c.used_count) || 0), 0),
    avgUsage: coupons.length > 0
      ? (coupons.reduce((s, c) => s + (Number(c.used_count) || 0), 0) / coupons.length).toFixed(1)
      : 0
  };

  if (loading && coupons.length === 0) {
    return <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={800}>Kupon Yönetimi</Typography>
          <Typography variant="body2" color="text.secondary">
            İndirim kodları, kullanım istatistikleri ve aktif/pasif kontrolü
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Yenile">
            <IconButton onClick={load}><RefreshIcon /></IconButton>
          </Tooltip>
          <Button
            variant="contained" startIcon={<AddIcon />}
            onClick={() => { setForm(emptyForm); setOpen(true); }}
          >
            Yeni Kupon
          </Button>
        </Box>
      </Box>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      {/* KPI satırı */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">Toplam Kupon</Typography>
            <Typography variant="h4" fontWeight={800}>{kpi.total}</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">Şu An Kullanılabilir</Typography>
            <Typography variant="h4" fontWeight={800} color="#10b981">{kpi.active}</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">Toplam Kullanım</Typography>
            <Typography variant="h4" fontWeight={800} color="#1565C0">{kpi.totalUsage}</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">Ort. Kullanım / Kupon</Typography>
            <Typography variant="h4" fontWeight={800} color="#f59e0b">{kpi.avgUsage}</Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Tablo */}
      <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: "#f8fafc" }}>
            <TableRow>
              <TableCell><strong>Kod</strong></TableCell>
              <TableCell><strong>Tip / Değer</strong></TableCell>
              <TableCell><strong>Min Sepet</strong></TableCell>
              <TableCell><strong>Max İnd.</strong></TableCell>
              <TableCell><strong>Kullanım</strong></TableCell>
              <TableCell><strong>Bitiş</strong></TableCell>
              <TableCell><strong>Segment</strong></TableCell>
              <TableCell align="center"><strong>Aktif</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {coupons.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  Henüz kupon yok. "Yeni Kupon" ile başlayın.
                </TableCell>
              </TableRow>
            )}
            {coupons.map(c => {
              const meta = TYPE_META[c.type] || { label: c.type, color: "#64748b", fmt: (v) => v };
              const expired = isExpired(c);
              const limitFull = isLimitFull(c);
              return (
                <TableRow key={c.id} hover sx={{
                  opacity: (!c.is_active || expired || limitFull) ? 0.55 : 1
                }}>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <LocalOfferIcon sx={{ color: meta.color, fontSize: 18 }} />
                      <Typography fontFamily="monospace" fontWeight={700}>{c.code}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={meta.label}
                      size="small"
                      sx={{ bgcolor: meta.color, color: "white", fontSize: 11, mr: 1 }}
                    />
                    <strong>{meta.fmt(c.value)}</strong>
                  </TableCell>
                  <TableCell>{fmtMoney(c.min_order_amount)}</TableCell>
                  <TableCell>{fmtMoney(c.max_discount)}</TableCell>
                  <TableCell>
                    {c.used_count || 0}
                    {c.usage_limit ? ` / ${c.usage_limit}` : " / ∞"}
                    {limitFull && <Chip label="DOLU" size="small" color="warning" sx={{ ml: 1, height: 18, fontSize: 10 }} />}
                  </TableCell>
                  <TableCell>
                    {fmtDate(c.expires_at)}
                    {expired && <Chip label="SÜRE DOLDU" size="small" color="error" sx={{ ml: 1, height: 18, fontSize: 10 }} />}
                  </TableCell>
                  <TableCell>
                    {c.segment ? <Chip label={c.segment} size="small" variant="outlined" /> : "—"}
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={!!c.is_active}
                      onChange={() => toggle(c.id)}
                      color="success"
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      {/* Yeni Kupon Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Yeni Kupon Oluştur</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={0}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Kupon Kodu *" value={form.code}
                onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
                inputProps={{ style: { fontFamily: "monospace", fontWeight: 700 } }}
                placeholder="ORN: YAZ2026"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select fullWidth label="Tip *" value={form.type}
                onChange={e => setForm({ ...form, type: e.target.value })}
              >
                <MenuItem value="percent">Yüzde (%)</MenuItem>
                <MenuItem value="fixed">Sabit Tutar (₺)</MenuItem>
                <MenuItem value="free_shipping">Ücretsiz Kargo</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth type="number" label="Değer *"
                value={form.value}
                onChange={e => setForm({ ...form, value: e.target.value })}
                helperText={form.type === "percent" ? "0-100 arası %" : form.type === "fixed" ? "₺ tutarı" : "0 girin"}
                disabled={form.type === "free_shipping"}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth type="date" label="Bitiş Tarihi"
                InputLabelProps={{ shrink: true }}
                value={form.expires_at}
                onChange={e => setForm({ ...form, expires_at: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth type="number" label="Min Sepet Tutarı (₺)"
                value={form.min_order_amount}
                onChange={e => setForm({ ...form, min_order_amount: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth type="number" label="Max İndirim (₺)"
                value={form.max_discount}
                onChange={e => setForm({ ...form, max_discount: e.target.value })}
                helperText="Sadece % tipinde anlamlı"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth type="number" label="Toplam Kullanım Limiti"
                value={form.usage_limit}
                onChange={e => setForm({ ...form, usage_limit: e.target.value })}
                helperText="Boş = sınırsız"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth type="number" label="Kişi Başı Limit"
                value={form.per_user_limit}
                onChange={e => setForm({ ...form, per_user_limit: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Segment (opsiyonel)"
                value={form.segment}
                onChange={e => setForm({ ...form, segment: e.target.value })}
                helperText="Sadece belirli segment kullanıcılarına özel: horeca, market, vb."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>İptal</Button>
          <Button variant="contained" onClick={submit} disabled={saving}>
            {saving ? "Kaydediliyor..." : "Oluştur"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
